import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  BACKUP_ENTITY,
  BACKUP_GLOBAL_SCOPE_VALUE,
  BackupApi,
  companyIdSelector,
  DrawerType,
  Log,
  RydeProcessTitle,
  useDrawers,
  warnAndToastSimpleResult,
  type IBackupRunOptions,
  type ISelectListItem,
} from "@ryde/shared";

import {
  BackupCreateForm,
  backupCreateValidationSchema,
  type IBackupCreateForm,
} from "./backup-create-drawer.models";

/**
 * Drives the "Back up now" drawer: loads what can be backed up, starts everything selected, and
 * hands the run to the shared process drawer. Every scope and entity is selected by default — the
 * drawer exists to take things OUT of a run, so an untouched one matches the nightly job.
 */
export const useBackupCreateDrawer = () => {
  const { t } = useTranslation();
  const { closeDrawer, openDrawer } = useDrawers();
  const activeCompanyId = useRecoilValue(companyIdSelector);
  const resolver = useYupValidationResolver(
    backupCreateValidationSchema,
    BACKUP_ENTITY,
  );

  const [options, setOptions] = useState<IBackupRunOptions | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isStarting, setIsStarting] = useState(false);

  const methods = useForm<IBackupCreateForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      [BackupCreateForm.scopes]: [],
      [BackupCreateForm.entityNames]: [],
    },
    resolver,
  });
  const { getValues, reset, watch } = methods;

  // Global first so it reads as the odd one out, then the companies in the order the API sorted them.
  const scopeItems: ISelectListItem[] = useMemo(() => {
    const companies = options?.companies ?? [];
    return [
      {
        value: BACKUP_GLOBAL_SCOPE_VALUE,
        label: t("backup.create.globalOption"),
      },
      ...companies.map(company => ({ value: company.id, label: company.name })),
    ];
  }, [options, t]);

  const entityItems: ISelectListItem[] = useMemo(
    () =>
      (options?.entities ?? []).map(entity => ({
        value: entity.value,
        label: entity.label,
      })),
    [options],
  );

  const loadOptions = useCallback(async () => {
    setIsLoadingOptions(true);
    try {
      const loaded = await BackupApi.getRunOptions();
      if (!loaded) {
        Log.errorAndToast(t("backup.create.optionsFailed"));
        return;
      }
      setOptions(loaded);
      // select everything up front: the drawer is for narrowing a run, not for building one
      reset({
        [BackupCreateForm.scopes]: [
          BACKUP_GLOBAL_SCOPE_VALUE,
          ...loaded.companies.map(company => company.id),
        ],
        [BackupCreateForm.entityNames]: loaded.entities.map(
          entity => entity.value,
        ),
      });
    } finally {
      setIsLoadingOptions(false);
    }
  }, [reset, t]);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  const selectedScopes = watch(BackupCreateForm.scopes);
  const selectedEntities = watch(BackupCreateForm.entityNames);
  const isStartDisabled =
    isLoadingOptions ||
    isStarting ||
    !selectedScopes?.length ||
    !selectedEntities?.length;

  const onStartClick = useEvent(async () => {
    const values = getValues();
    const scopes = values[BackupCreateForm.scopes] ?? [];
    // the sentinel is the only non-numeric entry, so splitting it back out needs no extra state
    const includeGlobal = scopes.includes(BACKUP_GLOBAL_SCOPE_VALUE);
    const companyIds = scopes.filter(
      (scope): scope is number => typeof scope === "number",
    );

    setIsStarting(true);
    try {
      const res = await BackupApi.run({
        companyIds,
        includeGlobal,
        entityNames: values[BackupCreateForm.entityNames] ?? [],
        // a global-only run has no company of its own, and a super admin has none either
        processCompanyId: activeCompanyId ?? undefined,
      });
      if (!res) {
        Log.errorAndToast(t("backup.create.noResponse"));
        return;
      }
      if (!res.success || !res.data) {
        warnAndToastSimpleResult(res, t);
        return;
      }

      Log.infoAndToast(t("backup.create.started"));
      closeDrawer(DrawerType.BackupCreate);
      openDrawer<DrawerType.ProcessDrawer>({
        drawerProps: {
          type: DrawerType.ProcessDrawer,
          processId: res.data.id,
          processTitle: RydeProcessTitle.BackupCreate,
          entity: BACKUP_ENTITY,
          companyId: res.data.companyId,
        },
      });
    } finally {
      setIsStarting(false);
    }
  });

  return {
    methods,
    scopeItems,
    entityItems,
    isLoadingOptions,
    isStarting,
    isStartDisabled,
    onStartClick,
  };
};
